import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Use relative URLs for API calls - works in both dev (proxy) and prod (same origin)
const getApiBase = () => {
  // In development, use the proxy (empty string works with package.json proxy)
  // In production on Railway, frontend is served from same origin as API
  return '';
};

const AdminObservability = () => {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchAllData = useCallback(async () => {
    const apiBase = getApiBase();
    try {
      const [healthRes, metricsRes, marketRes, dashboardRes] = await Promise.all([
        axios.get(`${apiBase}/api/health`).catch(e => ({ data: { error: e.message } })),
        axios.get(`${apiBase}/api/metrics`).catch(e => ({ data: { error: e.message } })),
        axios.get(`${apiBase}/api/market/status`).catch(e => ({ data: { error: e.message } })),
        axios.get(`${apiBase}/api/admin/dashboard`).catch(e => ({ data: { error: e.message } }))
      ]);

      setHealth(healthRes.data);
      setMetrics(metricsRes.data);
      setMarketStatus(marketRes.data);
      setDashboard(dashboardRes.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();

    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchAllData, 5000); // Refresh every 5 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchAllData]);

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    if (!seconds) return '0s';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (days > 0) return `${days}d ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const getStatusColor = (status) => {
    if (status === 'ok' || status === 'connected' || status === 'OPEN') return '#4ade80';
    if (status === 'degraded' || status === 'CLOSED') return '#fbbf24';
    return '#f87171';
  };

  const StatusBadge = ({ status, label }) => (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      borderRadius: '20px',
      backgroundColor: getStatusColor(status) + '20',
      color: getStatusColor(status),
      fontWeight: 'bold',
      fontSize: '14px'
    }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: getStatusColor(status)
      }}></span>
      {label || status}
    </span>
  );

  const Card = ({ title, children, status }) => (
    <div style={{
      backgroundColor: '#1a1a2e',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '16px',
      border: '1px solid #333'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{ margin: 0, color: '#fff', fontSize: '18px' }}>{title}</h3>
        {status && <StatusBadge status={status} />}
      </div>
      {children}
    </div>
  );

  const MetricRow = ({ label, value, unit }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #333'
    }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ color: '#fff', fontFamily: 'monospace' }}>
        {value}{unit && <span style={{ color: '#666', marginLeft: '4px' }}>{unit}</span>}
      </span>
    </div>
  );

  if (loading && !health) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
        Loading observability data...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <h2 style={{ margin: 0, color: '#fff' }}>🔍 System Observability</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>
          <button
            onClick={fetchAllData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            🔄 Refresh Now
          </button>
          {lastUpdate && (
            <span style={{ color: '#666', fontSize: '12px' }}>
              Last: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#f8717120',
          border: '1px solid #f87171',
          borderRadius: '8px',
          color: '#f87171',
          marginBottom: '16px'
        }}>
          Error: {error}
        </div>
      )}

      {/* Quick Status Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: '#1a1a2e',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #333'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>
            {health?.status === 'ok' ? '✅' : '⚠️'}
          </div>
          <div style={{ color: '#888', fontSize: '14px' }}>System Status</div>
          <div style={{ color: getStatusColor(health?.status), fontWeight: 'bold' }}>
            {health?.status?.toUpperCase() || 'UNKNOWN'}
          </div>
        </div>

        <div style={{
          backgroundColor: '#1a1a2e',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #333'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>👥</div>
          <div style={{ color: '#888', fontSize: '14px' }}>WebSocket Clients</div>
          <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: '24px' }}>
            {metrics?.connections?.websockets || health?.websockets?.socketio || 0}
          </div>
        </div>

        <div style={{
          backgroundColor: '#1a1a2e',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #333'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>
            {marketStatus?.status === 'OPEN' ? '📈' : '📉'}
          </div>
          <div style={{ color: '#888', fontSize: '14px' }}>Market</div>
          <div style={{ color: getStatusColor(marketStatus?.status), fontWeight: 'bold' }}>
            {marketStatus?.status || 'UNKNOWN'}
          </div>
        </div>

        <div style={{
          backgroundColor: '#1a1a2e',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #333'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚡</div>
          <div style={{ color: '#888', fontSize: '14px' }}>Response Time</div>
          <div style={{ color: '#4ade80', fontWeight: 'bold', fontSize: '24px' }}>
            {health?.responseTimeMs || 0}ms
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '16px'
      }}>
        {/* Health Card */}
        <Card title="🏥 Health Check" status={health?.status}>
          {health && !health.error ? (
            <>
              <MetricRow label="Uptime" value={formatUptime(health.uptime)} />
              <MetricRow label="DB Status" value={health.database?.status} />
              <MetricRow label="DB Latency" value={health.database?.latencyMs} unit="ms" />
              <MetricRow label="Trading Paused" value={health.market?.tradingPaused ? 'Yes' : 'No'} />
            </>
          ) : (
            <div style={{ color: '#f87171' }}>Failed to load health data</div>
          )}
        </Card>

        {/* Memory Card */}
        <Card title="💾 Memory Usage">
          {health?.memory ? (
            <>
              <MetricRow label="Heap Used" value={formatBytes(health.memory.heapUsed)} />
              <MetricRow label="Heap Total" value={formatBytes(health.memory.heapTotal)} />
              <MetricRow label="RSS" value={formatBytes(health.memory.rss)} />
              <MetricRow label="External" value={formatBytes(health.memory.external)} />
            </>
          ) : (
            <div style={{ color: '#888' }}>No memory data available</div>
          )}
        </Card>

        {/* Database Metrics Card */}
        <Card title="🗄️ Database Metrics">
          {metrics?.database ? (
            <>
              <MetricRow label="Total Users" value={metrics.database.totalUsers} />
              <MetricRow label="Recent Trades (10m)" value={metrics.database.recentTrades10min} />
              <MetricRow label="Open Positions" value={metrics.database.openPositions} />
              <MetricRow label="Total Stocks" value={metrics.database.totalStocks} />
              {metrics.database.pool && (
                <>
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #444' }}>
                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>Connection Pool</div>
                  </div>
                  <MetricRow label="Total Connections" value={metrics.database.pool.total} />
                  <MetricRow label="Idle" value={metrics.database.pool.idle} />
                  <MetricRow label="Waiting" value={metrics.database.pool.waiting} />
                </>
              )}
            </>
          ) : (
            <div style={{ color: '#888' }}>No database metrics available</div>
          )}
        </Card>

        {/* Party Dashboard Card */}
        <Card title="🎉 Party Status">
          {dashboard?.dashboard ? (
            <>
              <MetricRow label="Participants" value={dashboard.dashboard.participantCount} />
              <MetricRow label="Recent Trades" value={dashboard.dashboard.recentTradeCount} />
              <MetricRow label="Avg Balance" value={`$${Math.round(dashboard.dashboard.marketActivity?.avgBalance || 0).toLocaleString()}`} />
              <MetricRow label="Max Balance" value={`$${Math.round(dashboard.dashboard.marketActivity?.maxBalance || 0).toLocaleString()}`} />
              <MetricRow label="Min Balance" value={`$${Math.round(dashboard.dashboard.marketActivity?.minBalance || 0).toLocaleString()}`} />
            </>
          ) : (
            <div style={{ color: '#888' }}>No party data available</div>
          )}
        </Card>

        {/* Market Status Card */}
        <Card title="📊 Market Status" status={marketStatus?.status}>
          {marketStatus && !marketStatus.error ? (
            <>
              <MetricRow label="Status" value={marketStatus.status} />
              <MetricRow label="Trading Allowed" value={marketStatus.tradingAllowed ? 'Yes' : 'No'} />
              <MetricRow label="Events Paused" value={marketStatus.eventsPaused ? 'Yes' : 'No'} />
              <MetricRow label="Prices Paused" value={marketStatus.priceUpdatesPaused ? 'Yes' : 'No'} />
            </>
          ) : (
            <div style={{ color: '#888' }}>No market data available</div>
          )}
        </Card>

        {/* Server Info Card */}
        <Card title="🖥️ Server Info">
          {metrics?.server ? (
            <>
              <MetricRow label="Node Version" value={metrics.server.nodeVersion} />
              <MetricRow label="Uptime" value={formatUptime(metrics.server.uptime)} />
              <MetricRow label="Timestamp" value={new Date(metrics.timestamp).toLocaleTimeString()} />
            </>
          ) : (
            <div style={{ color: '#888' }}>No server info available</div>
          )}
        </Card>
      </div>

      {/* Raw JSON Section (collapsible) */}
      <details style={{ marginTop: '24px' }}>
        <summary style={{
          color: '#888',
          cursor: 'pointer',
          padding: '12px',
          backgroundColor: '#1a1a2e',
          borderRadius: '8px'
        }}>
          🔧 Raw API Responses (click to expand)
        </summary>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '16px',
          marginTop: '16px'
        }}>
          <div>
            <h4 style={{ color: '#888' }}>/api/health</h4>
            <pre style={{
              backgroundColor: '#0a0a14',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '12px',
              color: '#4ade80',
              maxHeight: '300px'
            }}>
              {JSON.stringify(health, null, 2)}
            </pre>
          </div>
          <div>
            <h4 style={{ color: '#888' }}>/api/metrics</h4>
            <pre style={{
              backgroundColor: '#0a0a14',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '12px',
              color: '#4ade80',
              maxHeight: '300px'
            }}>
              {JSON.stringify(metrics, null, 2)}
            </pre>
          </div>
        </div>
      </details>

      {/* API Endpoints Reference */}
      <div style={{
        marginTop: '24px',
        backgroundColor: '#1a1a2e',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #333'
      }}>
        <h3 style={{ color: '#fff', marginTop: 0 }}>📡 API Endpoints</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #333' }}>
              <th style={{ textAlign: 'left', padding: '8px', color: '#888' }}>Endpoint</th>
              <th style={{ textAlign: 'left', padding: '8px', color: '#888' }}>Description</th>
              <th style={{ textAlign: 'left', padding: '8px', color: '#888' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {[
              { path: '/health', desc: 'Simple health check' },
              { path: '/api/health', desc: 'Detailed health with DB' },
              { path: '/api/metrics', desc: 'Full system metrics' },
              { path: '/api/market/status', desc: 'Market status' },
              { path: '/api/admin/dashboard', desc: 'Party dashboard' }
            ].map(ep => (
              <tr key={ep.path} style={{ borderBottom: '1px solid #222' }}>
                <td style={{ padding: '8px', fontFamily: 'monospace', color: '#4ade80' }}>
                  {ep.path}
                </td>
                <td style={{ padding: '8px', color: '#888' }}>{ep.desc}</td>
                <td style={{ padding: '8px' }}>
                  <button
                    onClick={() => window.open(ep.path, '_blank')}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Open ↗
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminObservability;
